import {
  UTTable,
  UTButton,
  UTBox,
  UTTypography,
  ColorVariables,
} from '@group-link-one/gl-ui/utilities';
import type { ITableHeader, ITableItem } from '@group-link-one/gl-ui/utilities';
import { useEffect, useState } from 'react';
import { getPlatformPreferences } from '../../Services/Preferences.service';
import type { PlatformUserPreference, PreferencesRequest } from '../../Types/Preferences.types';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const PreferencesList = () => {
  const [preferences, setPreferences] = useState<PlatformUserPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchPreferences = async (params: PreferencesRequest = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getPlatformPreferences({ limit: 20, ...params });
      setPreferences(response.data);
      setNextPageToken(response.nextPageToken);
      setHasMore(response.hasMore);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch platform preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch platform preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (nextPageToken) {
      fetchPreferences({ nextPageToken });
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const formatTenantSlug = (tenantSlug: string): string => {
    return tenantSlug
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const tableHeaders: ITableHeader[] = [
    { key: 'userId', label: 'User ID', width: '10%' },
    { key: 'tenantSlug', label: 'Tenant', width: '20%' },
    { key: 'language', label: 'Language', width: '15%' },
    { key: 'theme', label: 'Theme', width: '15%' },
    { key: 'createdAt', label: 'Created At', width: '20%' },
    { key: 'updatedAt', label: 'Updated At', width: '20%' },
  ];

  const tableItems: ITableItem[] = preferences.map(item => ({
    id: `${item.userId}-${item.tenantSlug}`,
    userId: item.userId,
    tenantSlug: formatTenantSlug(item.tenantSlug),
    language: item.language ?? '-',
    theme: item.theme ?? '-',
    createdAt: new Date(item.createdAt).toLocaleString(),
    updatedAt: new Date(item.updatedAt).toLocaleString(),
  }));

  const renderCell = (key: string, value: unknown) => {
    if (key === 'language' || key === 'theme') {
      const isEmpty = value === '-';
      return (
        <UTBox
          padding='4px 8px'
          borderRadius='4px'
          backgroundColor={
            isEmpty
              ? ColorVariables.BASE.BASE_COLOR_TERTIARY_BACKGROUND
              : ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND
          }
          width='fit-content'
        >
          <UTTypography
            text={value as string}
            color={ColorVariables.COLOR.COLOR_QUINARY_BACKGROUND}
            fontSize='12px'
            fontWeight={600}
          />
        </UTBox>
      );
    }

    return value as string;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <UTBox display='flex' justifyContent='center' alignItems='center' padding='40px'>
          <UTTypography>Loading preferences...</UTTypography>
        </UTBox>
      );
    }

    if (error) {
      return (
        <UTBox
          flexDirection='column'
          gap='12px'
          padding='20px'
          borderRadius='8px'
          backgroundColor='rgba(255, 0, 0, 0.1)'
          color='#d32f2f'
        >
          <UTTypography fontWeight='600'>Error:</UTTypography>
          <UTTypography fontSize='14px' lineHeight='1.5'>
            {error}
          </UTTypography>
        </UTBox>
      );
    }

    return (
      <UTBox flexDirection='column' gap='16px' width='100%' flex='1'>
        <UTTable
          headers={tableHeaders}
          items={tableItems}
          isLoading={isLoading}
          colorAlternate={false}
          renderCell={renderCell}
        />
        {hasMore && (
          <UTBox display='flex' justifyContent='center' padding='16px'>
            <UTBox maxWidth={200}>
              <UTButton
                variant='secondary'
                text='Load More'
                onClick={handleLoadMore}
                icon='Arrows.ArrowDown'
              />
            </UTBox>
          </UTBox>
        )}
      </UTBox>
    );
  };

  return (
    <MainContent
      id='platform-preferences-main'
      topTitle={{
        title: 'Preferences',
        subtitle: 'View platform user preferences saved in the database.',
      }}
    >
      <UTBox flexDirection='column' gap='20px' width='100%' height='100%' flex='1'>
        {renderContent()}
      </UTBox>
    </MainContent>
  );
};

export { PreferencesList };
